import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { serverTimestamp } from 'firebase/firestore';
import { storage, functions, auth } from '../../../core/firebase';
import { useAppContext } from '../../../core/App';
import { useCurriculum } from '../../../core/context/CurriculumContext';
import { useTelemetry } from '../../../core/hooks/useTelemetry';

const gradeResumeFunction = httpsCallable(functions, 'gradeResume');

export const useResumeEngine = () => {
    // PATCH: Extracted userProfile to access the tenant ID
    const { authUser, userProfile, callApi, createNewResumeReport, updateLatestResumeReport, resumeReports } = useAppContext();
    const { saveAiAnalysisToSubCollection } = useCurriculum() || {};
    const { transmitTelemetry } = useTelemetry();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // AI Analysis States
    const [roleAnalysis, setRoleAnalysis] = useState(null);
    const [resumeAnalysis, setResumeAnalysis] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Job Matching States
    const [jobMatches, setJobMatches] = useState([]);
    const [isFindingJobs, setIsFindingJobs] = useState(false);

    const sanitizeReport = (data) => {
        if (!data) return null;
        return {
            ...data,
            overall_score: data.overall_score || 0,
            heatmap_rationales: Array.isArray(data.heatmap_rationales) ? data.heatmap_rationales : [],
            key_strengths: Array.isArray(data.key_strengths) ? data.key_strengths : [],
            improvements: Array.isArray(data.improvements) ? data.improvements : [],
            ats_brief: data.ats_brief || "Layout analysis pending.",
            metric_analysis: Array.isArray(data.metric_analysis) ? data.metric_analysis : [],
            cultural_translator: data.cultural_translator || "Context analysis pending.",
            star_audit: Array.isArray(data.star_audit) ? data.star_audit : [], 
            authenticity_check: data.authenticity_check || { cliches: [], fix: "No issues detected." },
            keyword_gap: data.keyword_gap || { systems: [], missing_skills: [] },
            action_verb_analysis: data.action_verb_analysis || { strong_verbs_used: [], weak_verbs_detected: [] },
            verb_pivot: data.verb_pivot || "Tone analysis pending.",
            positioning_shift: data.positioning_shift || "Positioning recalibration pending.",
            regional_context: data.regional_context || null,
            target_titles: Array.isArray(data.target_titles) ? data.target_titles : []
        };
    };

    const generateInitialReport = async (resumeText, jobDescription) => {
        try {
            const safeResumeText = resumeText.substring(0, 5000);
            const safeJD = jobDescription || "N/A";
            
            if (resumeReports && resumeReports.length > 0) {
                const cachedReport = resumeReports.find(r => r.resume === safeResumeText && r.jobDescription === safeJD);
                if (cachedReport && cachedReport.overall_score) {
                    console.log("[useResumeEngine] Deterministic Cache Hit. Bypassing LLM execution.");
                    return sanitizeReport(cachedReport);
                }
            }

            const result = await gradeResumeFunction({ resume: resumeText, jobDescription: jobDescription || "" });
            return sanitizeReport(result.data);
        } catch (err) {
            console.error("API Sync Failed:", err);
            throw err;
        }
    };

    const finalizeAndSaveReport = async (resumeText, jobDescription, tempReportCard, onGradeComplete) => {
        const targetUser = authUser || auth.currentUser;
        setIsLoading(true);
        setError(null);

        try {
            if (targetUser) {
                let downloadURL = "backup-only";
                try {
                    const timestamp = Date.now();
                    const storageRef = ref(storage, `resumes/${targetUser.uid}/${timestamp}_original.txt`);
                    await uploadString(storageRef, resumeText); 
                    downloadURL = await getDownloadURL(storageRef);
                } catch (storageErr) {
                    console.warn("STORAGE BLOCKED: Continuing with text-only save.", storageErr.message);
                }
                
                const rawPayload = {
                    resumeUrl: downloadURL,
                    jobDescription: jobDescription || "N/A",
                    resume: resumeText.substring(0, 5000), 
                    overall_score: Number(tempReportCard.overall_score) || 0,
                    heatmap_rationales: tempReportCard.heatmap_rationales || [],
                    ats_brief: tempReportCard.ats_brief || "N/A",
                    metric_analysis: tempReportCard.metric_analysis || [],
                    star_audit: tempReportCard.star_audit || [],
                    positioning_shift: tempReportCard.positioning_shift || "N/A",
                    key_strengths: tempReportCard.key_strengths || [],
                    improvements: tempReportCard.improvements || [],
                    cultural_translator: tempReportCard.cultural_translator || "Context analysis pending.",
                    authenticity_check: tempReportCard.authenticity_check || { cliches: [], fix: "No issues detected." },
                    keyword_gap: tempReportCard.keyword_gap || { systems: [], missing_skills: [] },
                    action_verb_analysis: tempReportCard.action_verb_analysis || { strong_verbs_used: [], weak_verbs_detected: [] },
                    verb_pivot: tempReportCard.verb_pivot || "Tone analysis pending.",
                    regional_context: tempReportCard.regional_context || null,
                    target_titles: tempReportCard.target_titles || [],
                    // --- O(1) DENORMALIZATION PATCH ---
                    enterpriseTenantId: userProfile?.enterpriseTenantId || 'public'
                };

                const sanitizedData = JSON.parse(JSON.stringify(rawPayload));
                const finalDoc = { ...sanitizedData, timestamp: serverTimestamp() };
                
                if (typeof createNewResumeReport === 'function') {
                    await createNewResumeReport(finalDoc);
                } else {
                    console.warn("[useResumeEngine] createNewResumeReport callback dropped from context.");
                }

                if (typeof transmitTelemetry === 'function') {
                    await transmitTelemetry({
                        moduleId: 'resume-grader',
                        rawInteractions: [{ status: 'success', score: sanitizedData.overall_score }]
                    });
                }
            }
            
            if (typeof onGradeComplete === 'function') {
                onGradeComplete(tempReportCard);
            }
            
            return tempReportCard;
        } catch (err) {
            setError(`Critical Error: ${err.message}`);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const analyzeRoleRisk = async (jobDescription, reportId) => {
        setIsProcessing(true);
        try {
            const systemInstruction = `ROLE: Market Automation Analyst. TASK: Analyze ONLY the provided JOB DESCRIPTION for automation risk. OUTPUT JSON: { "score": "High (Safe)|Medium|Low", "summary": "Outlook for this role.", "resilient_tasks": ["Human-required duties"], "at_risk_tasks": ["Automation-prone duties"] }`;
            const res = await callApi({ jobDescription }, systemInstruction);
            setRoleAnalysis(res);

            if (authUser) {
                if (typeof transmitTelemetry === 'function') {
                    await transmitTelemetry({ moduleId: 'USE_LONGEVITY_ANALYST', rawInteractions: [{ status: 'success', type: 'role_analysis' }] });
                }
                if (reportId && typeof saveAiAnalysisToSubCollection === 'function') {
                    await saveAiAnalysisToSubCollection(reportId, 'market_risk', res);
                }
            }
        } catch (e) {
            console.error(e);
            throw new Error("Role analysis failed.");
        } finally {
            setIsProcessing(false);
        }
    };

    const analyzeResumeResilience = async (resumeText, reportId) => {
        setIsProcessing(true);
        try {
            const systemInstruction = `ROLE: Career Resilience Coach. TASK: Analyze ONLY the user's RESUME for professional moats against AI. OUTPUT JSON: { "score": "High|Medium|Low", "summary": "Personal resilience outlook.", "resilient_skills": ["Human Moats in resume"], "at_risk_skills": ["Skills prone to automation"], "strategic_advice": "A 5-year strategic evolution plan for this specific candidate to stay relevant." }`;
            const res = await callApi({ resume: resumeText }, systemInstruction);
            setResumeAnalysis(res);

            if (authUser) {
                if (typeof transmitTelemetry === 'function') {
                    await transmitTelemetry({ moduleId: 'USE_LONGEVITY_ANALYST', rawInteractions: [{ status: 'success', type: 'resume_analysis' }] });
                }
                if (reportId && typeof saveAiAnalysisToSubCollection === 'function') {
                    await saveAiAnalysisToSubCollection(reportId, 'personal_armor', res);
                }
            }
        } catch (e) {
            console.error(e);
            throw new Error("Resume analysis failed.");
        } finally {
            setIsProcessing(false);
        }
    };

    const findJobMatches = async (resumeText) => {
        setIsFindingJobs(true);
        try {
            const systemInstruction = `ROLE: Career Coach. Identify 5 modern roles. SCHEMA: { "matches": [ { "title": "String", "relevance": "High"|"Medium", "reason": "String" } ] }`;
            const res = await callApi({ resume: resumeText }, systemInstruction);
            setJobMatches(res.matches || []);
            if (authUser && typeof updateLatestResumeReport === 'function') {
                updateLatestResumeReport({ jobMatches: res.matches });
            }
        } catch (e) {
            throw new Error("Could not find matches.");
        } finally {
            setIsFindingJobs(false);
        }
    };

    return {
        isLoading, error, setError,
        isProcessing, roleAnalysis, setRoleAnalysis, resumeAnalysis, setResumeAnalysis,
        isFindingJobs, jobMatches, setJobMatches,
        generateInitialReport, finalizeAndSaveReport,
        analyzeRoleRisk, analyzeResumeResilience, findJobMatches
    };
};